import React, { useEffect, useRef, useState } from 'react';
import { collabApi, requestChat, avatarStyle } from '../utils/collab';

/**
 * "Discuss with…" — the right-click menu on a matrix row.
 *
 * It lists the ACCOUNT DIRECTORY, not just who is online: the person who can answer
 * "was this test ever run?" is often the one who has gone home, and a menu that only
 * offered the three people currently on the rail would send you back to email.
 *
 * The directory is fetched when the menu opens rather than held by the page — this is a
 * rare interaction, and a matrix page that pre-loaded a staff list on every render would
 * be paying for it constantly.
 *
 * What it hands the dock is a CONTEXT, not a rendered card: the panel id and the row's
 * key. The title the recipient sees is resolved on their side (Critical Note #71).
 */
function DiscussMenu({ at, context, onClose }) {
  const [people, setPeople] = useState(null);
  const [error, setError] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    collabApi.directory()
      .then((data) => { if (alive) setPeople(data.people || []); })
      .catch((err) => { if (alive) { setPeople([]); setError(err.message || 'Could not read the directory.'); } });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  // Keep the menu on screen when the click was near the right or bottom edge.
  const style = {
    left: Math.min(at.x, window.innerWidth - 248),
    top: Math.min(at.y, window.innerHeight - 340),
  };

  const pick = (userId) => {
    onClose();
    requestChat(userId, context);
  };

  return (
    <div className="collab-row-menu" style={style} ref={ref} role="menu">
      <div className="collab-row-menu-head">
        Discuss {context?.focus?.key || 'this row'}
      </div>

      {people === null && <div className="collab-row-menu-empty">Loading people…</div>}

      {people && people.length === 0 && (
        <div className="collab-row-menu-empty">
          {error || 'Nobody else from this account has signed in yet.'}
        </div>
      )}

      {people && people.map((p) => (
        <button
          key={p.user_id}
          type="button"
          className="collab-row-menu-item"
          role="menuitem"
          onClick={() => pick(p.user_id)}
        >
          <span
            className={`collab-av${p.online ? ' collab-av--online' : ''}`}
            style={{ ...avatarStyle(p.avatar_color), width: 24, height: 24, fontSize: 10 }}
            aria-hidden="true"
          >
            {p.initials}
          </span>
          <span>{p.display_name}</span>
        </button>
      ))}
    </div>
  );
}

export default DiscussMenu;
